import { Fragment } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatoMoneda, formatoFecha, FiltroFecha, FiltroNumerico, FiltroMultiSelect, FiltroBoolean, FiltroTexto, getOpcionesFiltroBoolean, getOpcionesFiltroTexto, getOpcionesFiltroNumerico, getOpcionesFiltroFecha, getOpcionesFiltroMultiSelect, obtenerMatchMode } from '../utils/TableTemplates';

const BR_TAG_REGEX = /<br\s*\/?>/gi;

/* Convierte '\n' o '<br />' (texto plano, no HTML crudo) en saltos de línea reales, sin usar dangerouslySetInnerHTML */
const renderMultiline = (value) => {
    if (value == null) return value;
    const text = String(value);
    const normalizado = text.replace(BR_TAG_REGEX, '\n');
    if (!normalizado.includes('\n')) return text;

    const partes = normalizado.split('\n');
    return partes.map((parte, index) => (
        <Fragment key={index}>
            {parte}
            {index < partes.length - 1 && <br />}
        </Fragment>
    ));
};

/* Resuelve las propiedades visuales y de contenido (Body) */
const resolveBodyProps = (config, key, templates) => {
    // Prioridad: Template externo
    if (config.bodyTemplate && templates[config.bodyTemplate]) {
        return { body: templates[config.bodyTemplate] };
    }
    // Body explícito en modelo
    if (config.body) {
        return { body: config.body };
    }
    // Tipos de datos especiales
    if (config.isCurrency) {
        return {
            body: (rowData) => formatoMoneda(rowData[key]),
            alignHeader: "left",
            className: "text-left",
            bodyStyle: { textAlign: 'left' } // Usamos una prop intermedia para mezclarla luego
        };
    }
    if (config.isDate) {
        return {
            body: (rowData) => formatoFecha(rowData[key]),
            bodyStyle: { textAlign: 'center' }
        };
    }
    // Default: texto plano, con soporte para saltos de línea ('\n' o '<br />' literal)
    return {
        body: (rowData) => renderMultiline(rowData[key])
    };
};

/* Resuelve el editor de la celda */
const resolveEditorProps = (config, templates) => {
    if (config.editorTemplate && templates[config.editorTemplate]) {
        return { editor: templates[config.editorTemplate] };
    }
    if (config.editor) {
        return { editor: config.editor };
    }
    return {};
};

/* Resuelve la configuración de filtros */
const resolveFilterProps = (config, key, catalogos) => {
    if (!config.filter) return {};

    const baseProps = {
        filter: true,
        sortField: config.sortField || key,
        filterField: config.filterField || key,
        filterPlaceholder: config.filterPlaceholder || `Buscar ${config.header}`,
        filterMatchMode: obtenerMatchMode(config),
        showFilterMatchModes: true
    };

    // Estrategia de filtros por tipo
    if (config.isDate) {
        return {
            ...baseProps,
            filterElement: FiltroFecha,
            filterMatchModeOptions: getOpcionesFiltroFecha()
        };
    }
    if (config.isNumeric || config.isCurrency) {
        return {
            ...baseProps,
            filterElement: (opts) => FiltroNumerico(opts, baseProps.filterPlaceholder),
            filterMatchModeOptions: getOpcionesFiltroNumerico()
        };
    }
    if (config.isMultiselect) {
        return {
            ...baseProps,
            filterElement: (opts) => FiltroMultiSelect(opts, catalogos[config.catalogKey] || []),
            filterMatchModeOptions: getOpcionesFiltroMultiSelect()
        };
    }
    if (config.isBoolean) {
        return {
            ...baseProps,
            filterElement: FiltroBoolean,
            filterMatchModeOptions: getOpcionesFiltroBoolean()
        };
    }

    // Default: Texto
    return {
        ...baseProps,
        filterElement: (opts) => FiltroTexto(opts, baseProps.filterPlaceholder),
        filterMatchModeOptions: getOpcionesFiltroTexto()
    };
};

/**
 * TablaDinamica: Componente reutilizable que renderiza una DataTable de PrimeReact basada en un objeto de configuración (Modelo).
 * 
 * @param {Object} modelo - Objeto de configuración de columnas (ej: Cliente model).
 * @param {Object} estadoTabla - El objeto retornado por useLazyParams (lazyParams, loading, etc).
 * @param {Array} datos - Array de datos a mostrar.
 * @param {Number} totalRegistros - Total de registros para la paginación.
 * @param {Object} catalogos - Objeto con arrays para filtros multiselect { empresas: [], ... }.
 * @param {ReactNode} children - Columnas manuales extra (ej: check de selección, botón expander).
 */
export const TablaDinamica = ({
    modelo,
    estadoTabla,
    datos,
    totalRegistros,
    catalogos = {},
    children, // Prop reservada, lo que se renderiza internamente
    acciones,
    cuerpoTemplates = {},
    editorTemplates = {},
    ...props // Props extra para pasar directo al DataTable (expandedRows, onRowToggle, etc.)
}) => {

    // Desestructuramos el estado del hook
    const { lazyParams, onLazyChange, loading } = estadoTabla;
    // Generamos las columnas usando los resolvers
    const columnasGeneradas = Object.keys(modelo).map((key) => {
        const config = modelo[key];
        if (config.table !== true) return null;

        // Obtener configuraciones parciales
        const bodyProps = resolveBodyProps(config, key, cuerpoTemplates);
        const editorProps = resolveEditorProps(config, editorTemplates);
        const filterProps = resolveFilterProps(config, key, catalogos);

        // Extraer estilos específicos para mezclarlos correctamente
        const { bodyStyle: specificBodyStyle, ...restBodyProps } = bodyProps;

        // Combinar todo en las props finales de la columna
        const finalColProps = {
            field: config.field || key,
            header: config.header,
            sortable: config.sort ?? false,
            frozen: config.frozen || false,
            style: { ...config.style, ...specificBodyStyle }, // Mezclamos estilos
            alignHeader: config.alignHeader || undefined,
            headerStyle: config.headerStyle || {},
            bodyStyle: { ...specificBodyStyle, ...config.bodyStyle },
            ...restBodyProps, // body, className, alignHeader
            ...editorProps, // editor
            ...filterProps // filter, filterElement, options
        };

        return <Column key={key} {...finalColProps} />;
    });

    return (
        <DataTable value={datos} loading={loading} totalRecords={totalRegistros} lazy paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            onPage={onLazyChange}
            onSort={onLazyChange}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            onFilter={onLazyChange}
            filters={lazyParams.filters}
            // Configuración Visual
            filterDisplay="row"
            removableSort
            dataKey={props.dataKey} // Campo que identifica a la columna (debe ser único)
            stripedRows
            scrollable // Para que las columnas frozen true se activen
            emptyMessage={
                <div className="p-4 text-center text-500">
                    <i className="pi pi-folder-open text-3xl mb-2 text-300 block"></i>
                    No se encontraron registros.
                </div>
            }
            tableStyle={{ minWidth: '100%' }}
            filterDelay={800} // Tiempo de delay antes de aplicar el filtro en ms
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            {...props} // Props adicionales (expandible, selección, etc.)
        >
            {/* Hijos manuales primero */}
            {children}
            {/* Render de columnas basadas en el modelo */}
            {columnasGeneradas}
            {/* Render de acciones al final del todo */}
            {acciones}
        </DataTable>
    );
};